import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography
} from "@mui/material";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import RequestQuoteRoundedIcon from "@mui/icons-material/RequestQuoteRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import StatCard from "../components/StatCard";
import { apiRequest } from "../utils/http";

const DashboardPage = () => {
  const [products, setProducts] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [about, setAbout] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [productResponse, quotationResponse, messageResponse, aboutResponse] = await Promise.all([
          apiRequest("/api/products"),
          apiRequest("/api/quotations").catch(() => ({ data: [] })),
          apiRequest("/api/contact").catch(() => ({ data: [] })),
          apiRequest("/api/about").catch(() => ({ data: null }))
        ]);

        setProducts(productResponse?.data || []);
        setQuotations(quotationResponse?.data || []);
        setMessages(messageResponse?.data || []);
        setAbout(aboutResponse?.data || null);
      } catch (loadError) {
        setError(loadError.message || "Unable to load dashboard data.");
      }
    };

    load();
  }, []);

  const availableProducts = useMemo(
    () => products.filter((item) => item.status === "available").length,
    [products]
  );

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} xl={3}>
          <StatCard
            label="Products"
            value={products.length}
            helper={`${availableProducts} currently available`}
            icon={<Inventory2RoundedIcon color="primary" />}
            accent="linear-gradient(180deg, #fff7ed 0%, #ffffff 100%)"
          />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <StatCard
            label="Quotations"
            value={quotations.length}
            helper="Customer quote requests in the pipeline"
            icon={<RequestQuoteRoundedIcon color="primary" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <StatCard
            label="Messages"
            value={messages.length}
            helper="Contact requests waiting for follow-up"
            icon={<EmailRoundedIcon color="primary" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <StatCard
            label="Storefront status"
            value="Live"
            helper="Frontend and API connected to the admin"
            icon={<TrendingUpRoundedIcon color="primary" />}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5">Recent Listings</Typography>
                <Chip label={`${products.length} products`} color="primary" variant="outlined" />
              </Stack>
              <Grid container spacing={2}>
                {products.slice(0, 6).map((product) => (
                  <Grid item xs={12} md={6} key={product._id}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        border: "1px solid rgba(226,232,240,0.9)",
                        display: "flex",
                        gap: 2,
                        alignItems: "center"
                      }}
                    >
                      <Box
                        component="img"
                        src={product.imageUrl}
                        alt={product.title}
                        sx={{ width: 82, height: 82, objectFit: "cover", borderRadius: 2 }}
                      />
                      <Box>
                        <Typography sx={{ fontWeight: 700 }}>{product.title}</Typography>
                        <Typography sx={{ color: "text.secondary" }}>NGN {Number(product.price || 0).toLocaleString()}</Typography>
                        <Chip
                          size="small"
                          label={product.status}
                          color={product.status === "available" ? "success" : "default"}
                          sx={{ mt: 1, textTransform: "capitalize" }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 2 }}>Brand Snapshot</Typography>
                <Typography sx={{ color: "text.secondary", lineHeight: 1.8 }}>
                  {about?.heroSubtitle || "Use this admin to keep product listings, about content, and incoming leads up to date."}
                </Typography>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 2 }}>Latest Messages</Typography>
                <List disablePadding>
                  {messages.slice(0, 4).map((message) => (
                    <ListItem key={message._id} disableGutters sx={{ py: 1.25 }}>
                      <ListItemText
                        primary={message.fullName || message.name || "New enquiry"}
                        secondary={message.message || message.subject || "Customer message"}
                      />
                    </ListItem>
                  ))}
                  {messages.length === 0 && (
                    <Typography sx={{ color: "text.secondary" }}>No messages yet.</Typography>
                  )}
                </List>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default DashboardPage;
